import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, arrayUnion, arrayRemove, query, where } from 'firebase/firestore';
import { logError } from '../utils/log';

export const TeamSetupPage = ({ userRole }) => {
    const [teams, setTeams] = useState([]);
    const [venues, setVenues] = useState([]);
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newTeamName, setNewTeamName] = useState('');
    const [newTeamHomeLocation, setNewTeamHomeLocation] = useState('');
    const [newTeamDivision, setNewTeamDivision] = useState('A');
    const [newTeamArchived, setNewTeamArchived] = useState(false);
    const [editTeamId, setEditTeamId] = useState('');
    const [originalHomeLocation, setOriginalHomeLocation] = useState(''); // To track changes
    const [editTeamName, setEditTeamName] = useState('');
    const [editTeamHomeLocation, setEditTeamHomeLocation] = useState('');
    const [editTeamDivision, setEditTeamDivision] = useState('A');
    const [editTeamArchived, setEditTeamArchived] = useState(false);
    const [deleteTeamId, setDeleteTeamId] = useState('');

    // State for Venue Management
    const [newVenueName, setNewVenueName] = useState('');
    const [newVenueBoards, setNewVenueBoards] = useState(1);
    const [newVenueAddress, setNewVenueAddress] = useState('');
    const [newVenueWebsite, setNewVenueWebsite] = useState('');
    const [newVenuePhone, setNewVenuePhone] = useState('');
    // State for editing venues
    const [editingVenueId, setEditingVenueId] = useState(null);
    const [editedVenueName, setEditedVenueName] = useState('');
    const [editedVenueBoards, setEditedVenueBoards] = useState(1);
    const [editedVenueAddress, setEditedVenueAddress] = useState('');
    const [editedVenueWebsite, setEditedVenueWebsite] = useState('');
    const [editedVenuePhone, setEditedVenuePhone] = useState('');

    const [newPlayerFirstName, setNewPlayerFirstName] = useState('');
    const [newPlayerLastName, setNewPlayerLastName] = useState('');
    const [newPlayerNickname, setNewPlayerNickname] = useState('');
    const [newPlayerDivision, setNewPlayerDivision] = useState('A');
    const [newPlayerArchived, setNewPlayerArchived] = useState(false);

    const [editPlayerId, setEditPlayerId] = useState('');
    const [editPlayerFirstName, setEditPlayerFirstName] = useState('');
    const [editPlayerLastName, setEditPlayerLastName] = useState('');
    const [editPlayerNickname, setEditPlayerNickname] = useState('');
    const [editPlayerDivision, setEditPlayerDivision] = useState('A');
    const [editPlayerArchived, setEditPlayerArchived] = useState(false);

    const [deletePlayerId, setDeletePlayerId] = useState('');

    const [mergeNonMemberId, setMergeNonMemberId] = useState('');
    const [mergeMemberId, setMergeMemberId] = useState('');

    const [message, setMessage] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch Teams
            const teamsCollectionRef = collection(db, "teams");
            const teamsSnapshot = await getDocs(teamsCollectionRef);
            const teamsList = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTeams(teamsList);

            // Fetch Players (Users)
            const usersCollectionRef = collection(db, "users");
            const usersSnapshot = await getDocs(usersCollectionRef);
            const usersList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPlayers(usersList);

            // Fetch Venues
            const venuesCollectionRef = collection(db, "venues");
            const venuesSnapshot = await getDocs(venuesCollectionRef);
            const venuesList = venuesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setVenues(venuesList);

        } catch (error) {
            logError("Error fetching data: ", error);
            setMessage("Failed to load data from the database.");
        }
        setLoading(false);
    };

    useEffect(() => {
        if (userRole === 'admin') {
            fetchData();
        }
    }, [userRole]);

    // Filter players for selects
    const members = players.filter(p => p.role === 'member' || p.role === 'admin');
    const nonMembers = players.filter(p => p.role !== 'member' && p.role !== 'admin');

    // Create a list of all player IDs that are currently on any team
    const assignedPlayerIds = new Set(teams.flatMap(team => team.members));
    const unassignedPlayers = players.filter(p => !assignedPlayerIds.has(p.id));

    // --- Team Management Functions ---
    const handleCreateTeam = async (e) => {
        e.preventDefault();
        if (!newTeamName.trim() || !newTeamHomeLocation.trim()) {
            setMessage('Team name and home location are required.');
            return;
        }
        try {
            const teamsCollectionRef = collection(db, "teams");
            await addDoc(teamsCollectionRef, {
                name: newTeamName,
                homeLocation: newTeamHomeLocation,
                division: newTeamDivision || 'A',
                archived: !!newTeamArchived,
                members: []
            });
            setNewTeamName('');
            setNewTeamHomeLocation('');
            setNewTeamDivision('A');
            setNewTeamArchived(false);
            setMessage(`Team "${newTeamName}" created successfully.`);
            fetchData(); // Refetch data to show the new team
        } catch (error) {
            setMessage("Error creating team. Please try again.");
            logError("Error creating team: ", error);
        }
    };

    const handleEditTeam = async (e) => {
        e.preventDefault();
        if (!editTeamId || !editTeamName.trim() || !editTeamHomeLocation.trim()) {
            setMessage('Please select a team and provide a new name and home location.');
            return;
        }

        // --- Venue Conflict Check ---
        if (editTeamHomeLocation !== originalHomeLocation) {
            // Define venue capacities. This could also be stored in Firestore.
            const VENUE_CAPACITY = {
                'Down Lo': 3,
                'Nash\'s Restaurant': 2,
                'Argus': 1
            };

            const schedulesRef = collection(db, "schedules");
            const q = query(schedulesRef, where("teams", "array-contains", editTeamId));
            const scheduleSnapshots = await getDocs(q);

            for (const scheduleDoc of scheduleSnapshots.docs) {
                const schedule = scheduleDoc.data();
                // Ignore archived schedules during conflict check
                if (schedule.archived) {
                    continue;
                }

                for (const week of schedule.weeks) {
                    const gamesAtNewVenue = week.games.filter(game => game.venue === editTeamHomeLocation).length;
                    const isTeamPlayingHomeThisWeek = week.games.some(game => game.homeTeamId === editTeamId);

                    if (isTeamPlayingHomeThisWeek && gamesAtNewVenue >= (VENUE_CAPACITY[editTeamHomeLocation] || 1)) {
                        alert(
`Schedule Conflict Detected!

Changing the home venue to "${editTeamHomeLocation}" would overbook it in Week ${week.week} of the "${schedule.name}" schedule.

Please adjust the schedule manually in the Schedule Builder before changing this team's venue. The change has been cancelled.`
                        );
                        return; // Stop the update
                    }
                }
            }
        }
        // --- End Conflict Check ---

        const teamToEdit = teams.find(t => t.id === editTeamId);
        const isArchiving = !teamToEdit?.archived && editTeamArchived;

        try {
            const teamDocRef = doc(db, "teams", editTeamId);
            let updateData = {
                name: editTeamName,
                homeLocation: editTeamHomeLocation,
                division: editTeamDivision || 'A',
                archived: !!editTeamArchived,
            };

            if (isArchiving && teamToEdit.members.length > 0) {
                const formerPlayersToAdd = [];
                for (const memberId of teamToEdit.members) {
                    const player = players.find(p => p.id === memberId);
                    if (player) {
                        formerPlayersToAdd.push({ id: player.id, name: player.nickname || `${player.firstName} ${player.lastName}`.trim() });
                    }
                    const playerDocRef = doc(db, "users", memberId);
                    await updateDoc(playerDocRef, {
                        currentTeam: null,
                        formerTeams: arrayUnion({ id: teamToEdit.id, name: teamToEdit.name })
                    });
                }
                updateData.formerPlayers = arrayUnion(...formerPlayersToAdd);
                updateData.members = [];
            }

            await updateDoc(teamDocRef, updateData);

            setEditTeamId('');
            setEditTeamName('');
            setEditTeamHomeLocation('');
            setEditTeamDivision('A');
            setEditTeamArchived(false);
            setOriginalHomeLocation('');
            setMessage(`Team updated successfully.`);
            fetchData(); // Refetch data
        } catch (error) {
            setMessage("Error updating team.");
            logError("Error updating team: ", error);
        }
    };

    const handleDeleteTeam = async (e) => {
        e.preventDefault();
        if (deleteTeamId === '') {
            setMessage('Please select a team to delete.');
            return;
        }
        try {
            const teamDocRef = doc(db, "teams", deleteTeamId);
            await deleteDoc(teamDocRef);
            setMessage(`Team deleted successfully.`);
            setDeleteTeamId('');
            fetchData(); // Refetch data
        } catch (error) {
            setMessage("Error deleting team.");
            logError("Error deleting team: ", error);
        }
    };

    // --- Venue Management Functions ---
    const handleCreateVenue = async (e) => {
        e.preventDefault();
        if (!newVenueName.trim() || newVenueBoards < 1) {
            setMessage("Venue name and at least 1 board are required.");
            return;
        }
        try {
            const venuesCollectionRef = collection(db, "venues");
            await addDoc(venuesCollectionRef, {
                name: newVenueName,
                boards: newVenueBoards,
                address: newVenueAddress,
                website: newVenueWebsite,
                phone: newVenuePhone,
            });
            setMessage(`Venue "${newVenueName}" created successfully.`);
            setNewVenueName('');
            setNewVenueBoards(1);
            setNewVenueAddress('');
            setNewVenueWebsite('');
            setNewVenuePhone('');
            fetchData();
        } catch (error) {
            setMessage("Error creating venue.");
        }
    };

    const handleStartEditVenue = (venue) => {
        setEditingVenueId(venue.id);
        setEditedVenueName(venue.name);
        setEditedVenueBoards(venue.boards);
        setEditedVenueAddress(venue.address || '');
        setEditedVenueWebsite(venue.website || '');
        setEditedVenuePhone(venue.phone || '');
    };

    const handleCancelEditVenue = () => {
        setEditingVenueId(null);
    };

    const handleUpdateVenue = async (e) => {
        e.preventDefault();
        if (!editedVenueName.trim() || editedVenueBoards < 1) {
            setMessage("Venue name and at least 1 board are required.");
            return;
        }
        try {
            const venueDocRef = doc(db, "venues", editingVenueId);
            await updateDoc(venueDocRef, {
                name: editedVenueName,
                boards: editedVenueBoards,
                address: editedVenueAddress,
                website: editedVenueWebsite,
                phone: editedVenuePhone,
            });
            setMessage(`Venue "${editedVenueName}" updated successfully.`);
            handleCancelEditVenue();
            fetchData();
        } catch (error) {
            setMessage("Error updating venue.");
        }
    };

    const handleDeleteVenue = async (venueId) => {
        // Check if any team is using this venue
        const venueName = venues.find(v => v.id === venueId)?.name;
        if (teams.some(team => team.homeLocation === venueName)) {
            alert("Cannot delete venue: It is currently assigned as a home venue for one or more teams. Please reassign teams before deleting.");
            return;
        }
        if (window.confirm("Are you sure you want to delete this venue? This action cannot be undone.")) {
            try {
                await deleteDoc(doc(db, "venues", venueId));
                setMessage("Venue deleted successfully.");
                fetchData();
            } catch (error) {
                setMessage("Error deleting venue.");
            }
        }
    };

    // --- Player Management Functions ---
    const handleCreatePlayer = async (e) => {
        e.preventDefault();
        if (newPlayerFirstName.trim() === '') {
            setMessage('Player first name cannot be empty.');
            return;
        }
        try {
            const usersCollectionRef = collection(db, "users");
            const newPlayerData = {
                firstName: newPlayerFirstName,
                lastName: newPlayerLastName,
                nickname: newPlayerNickname.trim() || '',
                role: 'guest', // Distinguish from members
                profileImage: 'default',
                dateJoined: new Date().toISOString(),
                division: newPlayerDivision || 'A',
                archived: !!newPlayerArchived,
            };
            await addDoc(usersCollectionRef, newPlayerData);
            setMessage(`Player "${newPlayerFirstName}" created successfully.`);
            setNewPlayerFirstName('');
            setNewPlayerLastName('');
            setNewPlayerNickname('');
            setNewPlayerDivision('A');
            setNewPlayerArchived(false);
            fetchData(); // Refresh the player list
        } catch (error) {
            setMessage("Error creating player.");
            logError("Error creating player: ", error);
        }
    };

    const handleEditPlayer = async (e) => {
        e.preventDefault();
        if (!editPlayerId) {
            setMessage('Please select a player to edit.');
            return;
        }
        try {
            const playerDocRef = doc(db, "users", editPlayerId);
            await updateDoc(playerDocRef, {
                firstName: editPlayerFirstName,
                lastName: editPlayerLastName,
                nickname: editPlayerNickname,
                division: editPlayerDivision || 'A',
                archived: !!editPlayerArchived,
            });
            setMessage(`Player updated successfully.`);
            fetchData();
        } catch (error) {
            setMessage("Error updating player.");
            logError("Error updating player: ", error);
        }
    };

    const handleDeletePlayer = async (e) => {
        e.preventDefault();
        if (!deletePlayerId) {
            setMessage('Please select a player to delete.');
            return;
        }
        try {
            // This is a simplified delete. A full implementation would also remove the player from all teams.
            const playerDocRef = doc(db, "users", deletePlayerId);
            await deleteDoc(playerDocRef);
            setMessage(`Player deleted successfully.`);
            setDeletePlayerId('');
            fetchData();
        } catch (error) {
            setMessage("Error deleting player.");
            logError("Error deleting player: ", error);
        }
    };

    // --- Merge Non-Member Data ---
    const handleMergePlayers = async (e) => {
        e.preventDefault();
        if (!mergeNonMemberId || !mergeMemberId || mergeNonMemberId === mergeMemberId) {
            setMessage('Please select both a non-member and a member to merge.');
            return;
        }

        if (!window.confirm("Are you sure you want to merge this non-member's data? This will delete the non-member account and cannot be undone.")) {
            return;
        }

        setMessage("Starting merge process... This may take a moment.");

        try {
            // 1. Fetch all schedules to search for the non-member's ID
            const schedulesSnapshot = await getDocs(collection(db, "schedules"));

            for (const scheduleDoc of schedulesSnapshot.docs) {
                const schedule = scheduleDoc.data();
                let scheduleWasModified = false;

                // Iterate through weeks and games to find and replace the non-member ID
                const updatedWeeks = schedule.weeks.map(week => {
                    const updatedGames = week.games.map(game => {
                        if (game.results) {
                            // Update Player Assignments
                            Object.keys(game.results.playerAssignments).forEach(key => {
                                if (game.results.playerAssignments[key] === mergeNonMemberId) {
                                    game.results.playerAssignments[key] = mergeMemberId;
                                    scheduleWasModified = true;
                                }
                            });

                            // Merge Highlights
                            const nonMemberHighlights = game.results.highlights?.[mergeNonMemberId];
                            if (nonMemberHighlights) {
                                const memberHighlights = game.results.highlights[mergeMemberId] || {};

                                // Merge numeric counts
                                memberHighlights['180s'] = (parseInt(memberHighlights['180s'] || 0) + parseInt(nonMemberHighlights['180s'] || 0)).toString();
                                memberHighlights.c6 = (parseInt(memberHighlights.c6 || 0) + parseInt(nonMemberHighlights.c6 || 0)).toString();
                                memberHighlights.c7 = (parseInt(memberHighlights.c7 || 0) + parseInt(nonMemberHighlights.c7 || 0)).toString();
                                memberHighlights.c8 = (parseInt(memberHighlights.c8 || 0) + parseInt(nonMemberHighlights.c8 || 0)).toString();
                                memberHighlights.c9 = (parseInt(memberHighlights.c9 || 0) + parseInt(nonMemberHighlights.c9 || 0)).toString();

                                // Merge comma-separated strings
                                memberHighlights.tons = [memberHighlights.tons, nonMemberHighlights.tons].filter(Boolean).join(',');
                                memberHighlights.on80 = [memberHighlights.on80, nonMemberHighlights.on80].filter(Boolean).join(',');
                                memberHighlights.highOut = [memberHighlights.highOut, nonMemberHighlights.highOut].filter(Boolean).join(',');

                                game.results.highlights[mergeMemberId] = memberHighlights;
                                delete game.results.highlights[mergeNonMemberId];
                                scheduleWasModified = true;
                            }
                        }
                        return game;
                    });
                    return { ...week, games: updatedGames };
                });

                if (scheduleWasModified) {
                    const scheduleDocRef = doc(db, "schedules", scheduleDoc.id);
                    await updateDoc(scheduleDocRef, { weeks: updatedWeeks });
                }
            }

            // 2. Update team membership
            const teamsToUpdate = teams.filter(team => team.members.includes(mergeNonMemberId));
            for (const team of teamsToUpdate) {
                const teamDocRef = doc(db, "teams", team.id);
                // Use a single update to remove old and add new member
                await updateDoc(teamDocRef, { members: arrayRemove(mergeNonMemberId) });
                await updateDoc(teamDocRef, { members: arrayUnion(mergeMemberId) });

                // 3. Update the member's user document with their current team
                const memberDocRef = doc(db, "users", mergeMemberId);
                await updateDoc(memberDocRef, { currentTeam: { id: team.id, name: team.name } });
            }

            // 4. Finally, delete the non-member's user document
            const nonMemberDocRef = doc(db, "users", mergeNonMemberId);
            await deleteDoc(nonMemberDocRef);

            setMessage(`Successfully merged non-member data into member account.`);
            fetchData(); // Refresh all data
        } catch (error) {
            setMessage("Error merging players. Check the console for details.");
            logError("Error merging players: ", error);
        }
    };

    const handleSetCaptain = async (teamId, newCaptainId) => {
        const team = teams.find(t => t.id === teamId);
        if (!team) {
            setMessage("Error: Team not found.");
            return;
        }

        // Create the new members array with the new captain at the front
        const otherMembers = team.members.filter(id => id !== newCaptainId);
        const newMembersArray = [newCaptainId, ...otherMembers];

        const newCaptain = players.find(p => p.id === newCaptainId);
        const newCaptainName = newCaptain?.nickname || newCaptain?.firstName || 'the selected player';

        if (window.confirm(`Are you sure you want to set ${newCaptainName} as the new captain for ${team.name}?`)) {
            try {
                const teamDocRef = doc(db, "teams", teamId);
                await updateDoc(teamDocRef, { members: newMembersArray });
                setMessage("Captain updated successfully.");
                fetchData(); // Refresh data to show the change
            } catch (error) {
                setMessage("Error updating captain.");
                logError("Error setting captain: ", error);
            }
        }
    };

    // --- Add/Remove Player from Team ---
    const handleAddPlayerToTeam = async (teamId, playerId) => {
        const teamDocRef = doc(db, "teams", teamId);
        const playerDocRef = doc(db, "users", playerId);
        const team = teams.find(t => t.id === teamId);

        if (!team) {
            setMessage("Error: Team not found.");
            return;
        }

        try {
            // Step 1: Add the player to the team's member list
            await updateDoc(teamDocRef, { members: arrayUnion(playerId) });

            // Step 2: Update the player's profile with their new team
            await updateDoc(playerDocRef, {
                currentTeam: { id: teamId, name: team.name }
            });

            const playerAdded = players.find(p => p.id === playerId);
            setMessage(`${playerAdded?.nickname || playerAdded?.firstName} added to ${team.name}.`);
            fetchData(); // Refresh all data
        } catch (error) {
            setMessage("Error adding player to team.");
            logError("Error adding player: ", error);
        }
    };

    const handleRemovePlayerFromTeam = async (teamId, playerId) => {
        const teamDocRef = doc(db, "teams", teamId);
        const playerDocRef = doc(db, "users", playerId);
        const team = teams.find(t => t.id === teamId);
        const player = players.find(p => p.id === playerId);

        if (!team || !player) {
            setMessage("Error: Team or player not found.");
            return;
        }

        try {
            // Step 1: Remove the player from the team's member list
            await updateDoc(teamDocRef, { 
                members: arrayRemove(playerId),
                formerPlayers: arrayUnion({ id: playerId, name: player.nickname || `${player.firstName} ${player.lastName}`.trim() })
            });

            // Step 2: Add this team to the player's formerTeams list
            await updateDoc(playerDocRef, {
                currentTeam: null,
                formerTeams: arrayUnion({ id: teamId, name: team.name })
            });

            setMessage(`${player.nickname || player.firstName} removed from ${team.name}.`);
            fetchData(); // Refresh all data
        } catch (error) {
            setMessage("Error removing player from team.");
            logError("Error removing player from team: ", error);
        }
    };

    const handleRestoreTeam = async (teamId) => {
        if (!teamId) return;
        try {
            const teamDocRef = doc(db, "teams", teamId);
            await updateDoc(teamDocRef, { archived: false });
            setMessage("Team has been restored.");
            fetchData();
        } catch (error) {
            setMessage("Error restoring team.");
            logError("Error restoring team: ", error);
        }
    };


    if (userRole !== 'admin') {
        return <p className="text-xl text-danger text-center">Access Denied. Only administrators can view this page.</p>;
    }

    if (loading) {
        return <div className="text-center">Loading admin setup...</div>;
    }

    const currentTeams = teams.filter(team => !team.archived);
    const archivedTeams = teams.filter(team => team.archived);

    return (
        <div className="space-y-6">
            <h2 className="page-title heading-primary-1 text-4xl font-extrabold text-center mb-6">Team and Player Setup</h2>
            <p className="text-lg leading-relaxed text-muted text-center">
                Manage all teams and players in the league from here.
            </p>

            {message && (
                <div className={`p-3 rounded-md text-center ${message.startsWith('Error') ? 'bg-danger-soft text-danger' : 'bg-success-soft text-success'}`}>
                    {message}
                </div>
            )}

            {/* Team Management */}
            <div className="card rounded-lg shadow-md p-6">
                <h3 className="heading-primary-1 text-2xl font-bold mb-4">Team Management</h3>

                {/* Create Team */}
                <form onSubmit={handleCreateTeam} className="mb-6 border-b border-low pb-4">
                    <h4 className="text-xl font-semibold text-strong mb-2">Create New Team</h4>
                    <div className="flex flex-wrap items-end gap-4">
                        <input type="text" placeholder="New Team Name" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} className="flex-grow border border-low rounded w-full sm:w-auto py-2 px-3 surface-low" required />
                        <select value={newTeamHomeLocation} onChange={(e) => setNewTeamHomeLocation(e.target.value)} className="flex-grow border border-low rounded w-full sm:w-auto py-2 px-3 surface-low" required><option value="">Select Home Venue</option>{venues.map(venue => (<option key={venue.id} value={venue.name}>{venue.name}</option>))}</select>
                        <select value={newTeamDivision} onChange={(e) => setNewTeamDivision(e.target.value)} className="flex-grow border border-low rounded w-full sm:w-auto py-2 px-3 surface-low"><option value="A">Division A</option><option value="B">Division B</option></select>
                        <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" checked={newTeamArchived} onChange={e => setNewTeamArchived(e.target.checked)} /> Archived</label>
                        <button type="submit" className="btn btn-primary py-2 px-4 rounded-lg">Add Team</button>
                    </div>
                </form>

                {/* Edit Team */}
                <form onSubmit={handleEditTeam} className="mb-6 border-b border-low pb-4">
                    <h4 className="text-xl font-semibold text-strong mb-2">Edit Team</h4>
                    <div className="flex flex-wrap items-end gap-4">
                        <select value={editTeamId} onChange={(e) => { const team = teams.find(t => t.id === e.target.value); setEditTeamId(team?.id || ''); setEditTeamName(team?.name || ''); setEditTeamHomeLocation(team?.homeLocation || ''); setEditTeamDivision(team?.division || 'A'); setEditTeamArchived(!!team?.archived); setOriginalHomeLocation(team?.homeLocation || ''); }} className="flex-grow border border-low rounded w-full sm:w-auto py-2 px-3 surface-low"><option value="">Select Team to Edit</option>{teams.map(team => (<option key={team.id} value={team.id}>{team.name}</option>))}</select>
                        <input type="text" placeholder="New Name" value={editTeamName} onChange={(e) => setEditTeamName(e.target.value)} className="flex-grow border border-low rounded w-full sm:w-auto py-2 px-3 surface-low" required />
                        <select value={editTeamHomeLocation} onChange={(e) => setEditTeamHomeLocation(e.target.value)} className="flex-grow border border-low rounded w-full sm:w-auto py-2 px-3 surface-low" required><option value="">Select Home Venue</option>{venues.map(venue => (<option key={venue.id} value={venue.name}>{venue.name}</option>))}</select>
                        <select value={editTeamDivision} onChange={(e) => setEditTeamDivision(e.target.value)} className="flex-grow border border-low rounded w-full sm:w-auto py-2 px-3 surface-low"><option value="A">Division A</option><option value="B">Division B</option></select>
                        <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" checked={editTeamArchived} onChange={e => setEditTeamArchived(e.target.checked)} /> Archived</label>
                        <button type="submit" className="btn btn-primary py-2 px-4 rounded-lg">Save Changes</button>
                    </div>
                </form>

                {/* Delete Team */}
                <form onSubmit={handleDeleteTeam} className="mb-6">
                    <h4 className="text-xl font-semibold text-strong mb-2">Delete Team</h4>
                    <div className="flex flex-wrap items-end gap-4">
                        <select value={deleteTeamId} onChange={(e) => setDeleteTeamId(e.target.value)} className="flex-grow border border-low rounded w-full md:w-auto py-2 px-3 surface-low"><option value="">Select Team to Delete</option>{teams.map(team => (<option key={team.id} value={team.id}>{team.name}</option>))}</select>
                        <button type="submit" className="btn btn-danger py-2 px-4 rounded-lg">Delete Team</button>
                    </div>
                </form>

                {/* Teams List */}
                <h4 className="text-xl font-semibold text-strong mb-3">Current Teams</h4>
                <div className="overflow-x-auto">
                    <table className="min-w-max sm:min-w-full rounded-lg overflow-hidden border border-low">
                        <thead className="table-header">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider border-b border-low">Team Name</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider border-b border-low">Members</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider border-b border-low">Roster Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentTeams.map((team, index) => (
                                <tr key={team.id} className={index % 2 === 1 ? 'row-alt' : ''}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-strong align-top border-b border-low">{team.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted align-top border-b border-low">
                                        <ul className="space-y-2">
                                            {team.members.map((memberId, index) => {
                                                const player = players.find(p => p.id === memberId);
                                                return (
                                                    <li key={memberId} className="flex items-center justify-between">
                                                        <span>{player ? `${player.nickname || player.firstName} ${player.lastName}` : 'Unknown Player'} {index === 0 && <span className="text-xs font-bold text-warning">(Captain)</span>}</span>
                                                        {index > 0 && <button onClick={() => handleSetCaptain(team.id, memberId)} className="btn btn-warning ml-4 text-xs py-1 px-2 rounded">Set Captain</button>}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm align-top border-b border-low">
                                        <div className="flex flex-col space-y-2">
                                            <select onChange={(e) => { const playerId = e.target.value; if (playerId) { handleAddPlayerToTeam(team.id, playerId); } e.target.value = ''; }} className="border border-low rounded py-1 px-2 surface-low"><option value="">Add Player...</option>{unassignedPlayers.map(p => (<option key={p.id} value={p.id}>{p.nickname || p.firstName} {p.lastName}</option>))}</select>
                                            <select onChange={(e) => { const playerId = e.target.value; if (playerId) { handleRemovePlayerFromTeam(team.id, playerId); } e.target.value = ''; }} className="border border-low rounded py-1 px-2 surface-low"><option value="">Remove Player...</option>{players.filter(p => team.members.includes(p.id)).map(p => (<option key={p.id} value={p.id}>{p.nickname || p.firstName} {p.lastName}</option>))}</select>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Archived Teams List */}
                <h4 className="text-xl font-semibold text-strong mb-3 mt-6">Archived Teams</h4>
                <div className="overflow-x-auto">
                    <table className="min-w-max sm:min-w-full rounded-lg overflow-hidden border border-low">
                        <thead className="table-header">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider border-b border-low">Team Name</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider border-b border-low">Members</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider border-b border-low">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {archivedTeams.map((team, index) => (
                                <tr key={team.id} className={index % 2 === 1 ? 'row-alt' : ''}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-strong align-top border-b border-low">{team.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted align-top border-b border-low">
                                        <ul className="space-y-2">
                                            {(team.formerPlayers && team.formerPlayers.length > 0 ? team.formerPlayers : team.members.map(id => {
                                                const p = players.find(p => p.id === id);
                                                return p ? { id: p.id, name: p.nickname || `${p.firstName} ${p.lastName}`.trim() } : null;
                                            }).filter(Boolean)).map((player) => (
                                                <li key={player.id} className="flex items-center justify-between">
                                                    <span>{player.name || 'Unknown Player'}</span>
                                                </li>
                                            ))}
                                             {team.members.length === 0 && (!team.formerPlayers || team.formerPlayers.length === 0) && <li>No players on this team.</li>}
                                        </ul>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm align-top border-b border-low">
                                        <button onClick={() => handleRestoreTeam(team.id)} className="btn btn-primary py-1 px-2 rounded">Restore Team</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Player Management */}
            <div className="card rounded-lg shadow-md p-6">
                <h3 className="heading-primary-1 text-2xl font-bold mb-4">Player Management</h3>

                {/* Create Player */}
                <form onSubmit={handleCreatePlayer} className="mb-6 border-b border-low pb-4">
                    <h4 className="text-xl font-semibold text-strong mb-2">Create New Player</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input type="text" placeholder="First Name" value={newPlayerFirstName} onChange={(e) => setNewPlayerFirstName(e.target.value)} className="border border-low rounded py-2 px-3 surface-low" required />
                        <input type="text" placeholder="Last Name (Optional)" value={newPlayerLastName} onChange={(e) => setNewPlayerLastName(e.target.value)} className="border border-low rounded py-2 px-3 surface-low" />
                        <input type="text" placeholder="Nickname (Optional)" value={newPlayerNickname} onChange={(e) => setNewPlayerNickname(e.target.value)} className="border border-low rounded py-2 px-3 surface-low" />
                        <select value={newPlayerDivision} onChange={(e) => setNewPlayerDivision(e.target.value)} className="border border-low rounded py-2 px-3 surface-low"><option value="A">Division A</option><option value="B">Division B</option></select>
                        <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" checked={newPlayerArchived} onChange={e => setNewPlayerArchived(e.target.checked)} /> Archived</label>
                    </div>
                    <button type="submit" className="btn btn-primary mt-4 py-2 px-4 rounded-lg w-full">Add Player</button>
                </form>

                {/* Edit Player */}
                <form onSubmit={handleEditPlayer} className="mb-6 border-b border-low pb-4">
                    <h4 className="text-xl font-semibold text-strong mb-2">Edit Player</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <select value={editPlayerId} onChange={(e) => { setEditPlayerId(e.target.value); const selectedPlayer = players.find(p => p.id === e.target.value); setEditPlayerFirstName(selectedPlayer ? selectedPlayer.firstName : ''); setEditPlayerLastName(selectedPlayer ? selectedPlayer.lastName : ''); setEditPlayerNickname(selectedPlayer ? selectedPlayer.nickname || '' : ''); setEditPlayerDivision(selectedPlayer ? selectedPlayer.division || 'A' : 'A'); setEditPlayerArchived(!!selectedPlayer?.archived); }} className="border border-low rounded py-2 px-3 surface-low col-span-2"><option value="">Select Player to Edit</option>{players.map(player => (<option key={player.id} value={player.id}>{player.firstName} {player.lastName}{player.role !== 'member' && player.role !== 'admin' ? " (non-member)" : ""}</option>))}</select>
                        <input type="text" placeholder="New First Name" value={editPlayerFirstName} onChange={(e) => setEditPlayerFirstName(e.target.value)} className="border border-low rounded py-2 px-3 surface-low" />
                        <input type="text" placeholder="New Last Name (Optional)" value={editPlayerLastName} onChange={(e) => setEditPlayerLastName(e.target.value)} className="border border-low rounded py-2 px-3 surface-low" />
                        <input type="text" placeholder="New Nickname (Optional)" value={editPlayerNickname} onChange={(e) => setEditPlayerNickname(e.target.value)} className="border border-low rounded py-2 px-3 surface-low" />
                        <select value={editPlayerDivision} onChange={(e) => setEditPlayerDivision(e.target.value)} className="border border-low rounded py-2 px-3 surface-low"><option value="A">Division A</option><option value="B">Division B</option></select>
                        <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" checked={editPlayerArchived} onChange={e => setEditPlayerArchived(e.target.checked)} /> Archived</label>
                    </div>
                    <button type="submit" className="btn btn-primary mt-4 py-2 px-4 rounded-lg w-full">Save Changes</button>
                </form>

                {/* Delete Player */}
                <form onSubmit={handleDeletePlayer} className="mb-6">
                    <h4 className="text-xl font-semibold text-strong mb-2">Delete Player</h4>
                    <div className="flex flex-wrap items-end gap-4">
                        <select value={deletePlayerId} onChange={(e) => setDeletePlayerId(e.target.value)} className="flex-grow border border-low rounded w-full md:w-auto py-2 px-3 surface-low"><option value="">Select Player to Delete</option>{players.map(player => (<option key={player.id} value={player.id}>{player.firstName} {player.lastName}{player.role !== 'member' && player.role !== 'admin' ? " (non-member)" : ""}</option>))}</select>
                        <button type="submit" className="btn btn-danger py-2 px-4 rounded-lg">Delete Player</button>
                    </div>
                </form>

                {/* Players List */}
                <h4 className="text-xl font-semibold text-strong mb-3">Current Players</h4>
                <div className="overflow-x-auto">
                    <table className="min-w-max sm:min-w-full rounded-lg overflow-hidden border border-low">
                        <thead className="table-header"><tr><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider border-b border-low">Player Name</th><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider border-b border-low">Nickname</th><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider border-b border-low">Role</th><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider border-b border-low">Player ID</th></tr></thead>
                        <tbody>
                            {players.map((player, index) => (<tr key={player.id} className={index % 2 === 1 ? 'row-alt' : ''}><td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-strong border-b border-low">{player.firstName} {player.lastName}</td><td className="px-6 py-3 whitespace-nowrap text-sm text-muted border-b border-low">{player.nickname || 'N/A'}</td><td className="px-6 py-3 whitespace-nowrap text-sm text-muted border-b border-low">{player.role}</td><td className="px-6 py-3 whitespace-nowrap text-sm text-muted border-b border-low">{player.id}</td></tr>))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Merge Non-Member to Member */}
            <div className="card rounded-lg shadow-md p-6">
                <h3 className="heading-primary-1 text-2xl font-bold mb-4">Merge Non-Member Data</h3>
                <p className="text-lg leading-relaxed text-muted mb-4">
                    Merge a non-member's historical data (e.g., team affiliations) into a newly created or existing member account.
                </p>
                <form onSubmit={handleMergePlayers}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label htmlFor="nonMemberSelect" className="block text-muted text-sm font-bold mb-2">Select Non-Member:</label>
                            <select id="nonMemberSelect" value={mergeNonMemberId} onChange={(e) => setMergeNonMemberId(e.target.value)} className="border border-low rounded w-full py-2 px-3 surface-low"><option value="">Select a Non-Member</option>{nonMembers.map(player => (<option key={player.id} value={player.id}>{player.firstName} {player.lastName}</option>))}</select>
                        </div>
                        <div>
                            <label htmlFor="memberSelect" className="block text-muted text-sm font-bold mb-2">Select Target Member:</label>
                            <select id="memberSelect" value={mergeMemberId} onChange={(e) => setMergeMemberId(e.target.value)} className="border border-low rounded w-full py-2 px-3 surface-low"><option value="">Select a Member</option>{members.map(player => (<option key={player.id} value={player.id}>{player.firstName} {player.lastName}</option>))}</select>
                        </div>
                    </div>
                    <button type="submit" className="btn btn-secondary py-2 px-4 rounded-lg w-full">Merge Data</button>
                </form>
            </div>

            {/* Division Maintenance (Temporary) */}
            <div className="card rounded-lg shadow-md p-6">
                <h3 className="heading-primary-1 text-2xl font-bold mb-2">Division Maintenance (Temporary)</h3>
                <p className="text-sm text-muted mb-4">Ensure all players and teams have a Division field. Defaults to Division A.</p>
                <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={async () => {
                        try {
                            setMessage('Ensuring division fields...');
                            // Update Players: set division: 'A' where missing
                            const usersSnapshot = await getDocs(collection(db, 'users'));
                            let playersUpdated = 0;
                            for (const u of usersSnapshot.docs) {
                                const data = u.data();
                                if (!data.division) {
                                    await updateDoc(doc(db, 'users', u.id), { division: 'A' });
                                    playersUpdated++;
                                }
                            }
                            // Update Teams: set division: 'A' where missing
                            const teamsSnapshot = await getDocs(collection(db, 'teams'));
                            let teamsUpdated = 0;
                            for (const t of teamsSnapshot.docs) {
                                const tdata = t.data();
                                if (!tdata.division) {
                                    await updateDoc(doc(db, 'teams', t.id), { division: 'A' });
                                    teamsUpdated++;
                                }
                            }
                            setMessage(`Division coherence complete. Players updated: ${playersUpdated}. Teams updated: ${teamsUpdated}.`);
                            fetchData();
                        } catch (err) {
                            logError('Error ensuring division fields', err);
                            setMessage('Error ensuring division fields. Check console.');
                        }
                    }} className="btn btn-primary py-2 px-4 rounded-lg">Ensure Division Field</button>
                    <button onClick={() => {
                        const missingPlayers = players.filter(p => !p.division).length;
                        const missingTeams = teams.filter(t => !t.division).length;
                        setMessage(`Check: Missing division - Players: ${missingPlayers}, Teams: ${missingTeams}.`);
                    }} className="btn btn-secondary py-2 px-4 rounded-lg">Check Division Coverage</button>
                </div>
            </div>

            {/* Archive Maintenance (Temporary) */}
            <div className="card rounded-lg shadow-md p-6">
                <h3 className="heading-primary-1 text-2xl font-bold mb-2">Archive Maintenance (Temporary)</h3>
                <p className="text-sm text-muted mb-4">Backfill the archived flag on all players and teams. Defaults to Active (archived=false).</p>
                <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={async () => {
                        try {
                            setMessage('Ensuring archived flags...');
                            // Players
                            const usersSnapshot = await getDocs(collection(db, 'users'));
                            let playersUpdated = 0;
                            for (const u of usersSnapshot.docs) {
                                const data = u.data();
                                if (typeof data.archived === 'undefined') {
                                    await updateDoc(doc(db, 'users', u.id), { archived: false });
                                    playersUpdated++;
                                }
                            }
                            // Teams
                            const teamsSnapshot = await getDocs(collection(db, 'teams'));
                            let teamsUpdated = 0;
                            for (const t of teamsSnapshot.docs) {
                                const tdata = t.data();
                                if (typeof tdata.archived === 'undefined') {
                                    await updateDoc(doc(db, 'teams', t.id), { archived: false });
                                    teamsUpdated++;
                                }
                            }
                            setMessage(`Archive coherence complete. Players updated: ${playersUpdated}. Teams updated: ${teamsUpdated}.`);
                            fetchData();
                        } catch (err) {
                            logError('Error ensuring archived flags', err);
                            setMessage('Error ensuring archived flags. Check console.');
                        }
                    }} className="btn btn-primary py-2 px-4 rounded-lg">Ensure Archived Flag</button>
                    <button onClick={() => {
                        const missingPlayers = players.filter(p => typeof p.archived === 'undefined').length;
                        const missingTeams = teams.filter(t => typeof t.archived === 'undefined').length;
                        setMessage(`Check: Missing archived flag - Players: ${missingPlayers}, Teams: ${missingTeams}.`);
                    }} className="btn btn-secondary py-2 px-4 rounded-lg">Check Archived Coverage</button>
                </div>
            </div>

            {/* Venue Management (Moved to Bottom) */}
            <div className="card rounded-lg shadow-md p-6">
                <h3 className="heading-primary-1 text-2xl font-bold mb-4">Venue Management</h3>
                <form onSubmit={handleCreateVenue} className="mb-6 border-b border-low pb-4">
                    <h4 className="text-xl font-semibold text-strong mb-2">Create New Venue</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                        <input type="text" placeholder="Venue Name" value={newVenueName} onChange={(e) => setNewVenueName(e.target.value)} className="border border-low rounded py-2 px-3 surface-low" required />
                        <input type="number" placeholder="Number of Boards" value={newVenueBoards} onChange={(e) => setNewVenueBoards(parseInt(e.target.value, 10))} className="border border-low rounded py-2 px-3 surface-low" min="1" required />
                        <input type="text" placeholder="Address (Optional)" value={newVenueAddress} onChange={(e) => setNewVenueAddress(e.target.value)} className="border border-low rounded py-2 px-3 surface-low" />
                        <input type="text" placeholder="Website (Optional)" value={newVenueWebsite} onChange={(e) => setNewVenueWebsite(e.target.value)} className="border border-low rounded py-2 px-3 surface-low" />
                        <input type="text" placeholder="Phone (Optional)" value={newVenuePhone} onChange={(e) => setNewVenuePhone(e.target.value)} className="border border-low rounded py-2 px-3 surface-low" />
                        <button type="submit" className="btn btn-success py-2 px-4 rounded-lg h-10">Add Venue</button>
                    </div>
                </form>
                <h4 className="text-xl font-semibold text-strong mb-3">Current Venues</h4>
                <div className="overflow-x-auto">
                    <table className="min-w-full rounded-lg overflow-hidden border border-low">
                        <thead className="table-header">
                            <tr>
                               <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase border-b border-low">Venue</th>
                               <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase border-b border-low">Boards</th>
                               <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase border-b border-low">Address</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase border-b border-low">Website</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase border-b border-low">Phone</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase border-b border-low">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {venues.map((venue, index) => (
                                <tr key={venue.id} className={index % 2 === 1 ? 'row-alt' : ''}>
                                    {editingVenueId === venue.id ? (
                                        <td colSpan="6" className="p-4">
                                            <form onSubmit={handleUpdateVenue} className="space-y-2">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                                                    <input type="text" value={editedVenueName} onChange={e => setEditedVenueName(e.target.value)} className="p-1 border border-low rounded surface-low" placeholder="Name" />
                                                    <input type="number" value={editedVenueBoards} onChange={e => setEditedVenueBoards(parseInt(e.target.value, 10))} className="p-1 border border-low rounded w-24 surface-low" min="1" placeholder="Boards" />
                                                    <input type="text" value={editedVenueAddress} onChange={e => setEditedVenueAddress(e.target.value)} className="p-1 border border-low rounded surface-low" placeholder="Address" />
                                                    <input type="text" value={editedVenueWebsite} onChange={e => setEditedVenueWebsite(e.target.value)} className="p-1 border border-low rounded surface-low" placeholder="Website" />
                                                    <input type="text" value={editedVenuePhone} onChange={e => setEditedVenuePhone(e.target.value)} className="p-1 border border-low rounded surface-low" placeholder="Phone" />
                                                </div>
                                                <div className="flex gap-2">
                                                    <button type="submit" className="text-success text-sm font-bold hover:underline">Save</button>
                                                    <button type="button" onClick={handleCancelEditVenue} className="text-muted text-sm hover:underline">Cancel</button>
                                                </div>
                                            </form>
                                        </td>
                                    ) : (
                                        <><td className="px-6 py-4 border-b border-low">{venue.name}</td><td className="px-6 py-4 border-b border-low">{venue.boards}</td><td className="px-6 py-4 border-b border-low">{venue.address}</td><td className="px-6 py-4 border-b border-low">{venue.website}</td><td className="px-6 py-4 border-b border-low">{venue.phone}</td><td className="px-6 py-4 border-b border-low"><button onClick={() => handleStartEditVenue(venue)} className="text-info text-sm mr-4 hover:underline">Edit</button><button onClick={() => handleDeleteVenue(venue.id)} className="text-danger text-sm hover:underline">Delete</button></td></>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};